import { privateRoute } from "./utils/catalogueSeo";
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType, useParams } from "react-router-dom";
import { trackSessionStart, trackAddToCart as fireATC } from "./utils/analytics";
import { useCart } from "./hooks/useCart";
import SmoothScroll, { useSmoothScrollLenis } from "./components/common/SmoothScroll";
import { AuthProvider } from "./contexts/AuthContext";
import { SellerAuthProvider } from "./contexts/SellerAuthContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { ConsentProvider } from "./contexts/ConsentContext";
import CookieBanner from "./components/consent/CookieBanner";
import BackShortlyBanner from "./components/maintenance/BackShortlyBanner";
import MaintenanceNotice from "./components/maintenance/MaintenanceNotice";
import Nav from "./components/nav/Nav";
import Home from "./components/home/Home";
import Suits from "./components/suits/Suits";
import Shirts from "./components/shirts/Shirts";
import Shoes from "./components/shoes/Shoes";
import Newarrivals from "./components/newarrivals/Newarrivals";
import ProductDetailPage from "./components/product/ProductDetailPage";
import Cart from "./components/cart/Cart";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import NotFound from "./components/notfound/NotFound";
import { inboxPath, ordersPath } from "./utils/inboxPath";
import WishNotice from "./components/shared/WishNotice";
import CategoryPage from "./components/category/CategoryPage";
import SearchPage from "./components/search/SearchPage";
import ShopPage from "./components/shop/ShopPage";
import RailPage from "./components/rails/RailPage";
import Footer from "./components/footer/Footer";
import SiteBackground, { SiteBackgroundFade } from "./components/background/SiteBackground";
import lazyWithReload, { QuietChunkBoundary, RouteChunkBoundary } from "./utils/lazyWithReload";
import "./index.css";

// Pages most shoppers never open load only when visited (speed brief item 5,
// 2026-11-03): admin, the seller portal, checkout, the inbox and account
// pages, policy pages. Home, category, search, shop, product, cart and
// sign-in stay in the main bundle. lazyWithReload reloads once if an old tab
// asks for a chunk a deploy has replaced.
const Account = lazyWithReload(() => import("./components/account/Account"));
const WishesPage = lazyWithReload(() => import("./components/wishes/WishesPage"));
const InboxPage = lazyWithReload(() => import("./components/inbox/InboxPage"));
const Deliveries = lazyWithReload(() => import("./components/deliveries/Deliveries"));
const Checkout = lazyWithReload(() => import("./components/checkout/Checkout"));
const CardReturn = lazyWithReload(() => import("./components/checkout/CardReturn"));
const OrderTracking = lazyWithReload(() => import("./components/orders/OrderTracking"));
const Admin = lazyWithReload(() => import("./components/admin/Admin"));
const AddProductPage = lazyWithReload(() => import("./components/admin/AddProductPage"));
const SellLanding = lazyWithReload(() => import("./components/sell/SellLanding"));
const SellerApplicationWizard = lazyWithReload(() => import("./components/sell/apply/SellerApplicationWizard"));
const ApplicationStatusPage = lazyWithReload(() => import("./components/sell/apply/ApplicationStatusPage"));
const SellerLoginPage = lazyWithReload(() => import("./components/sell/portal/SellerLoginPage"));
const MyListings = lazyWithReload(() => import("./components/sell/portal/MyListings"));
const SellerInsights = lazyWithReload(() => import("./components/sell/portal/SellerInsights"));
const ShopProfilePage = lazyWithReload(() => import("./components/sell/portal/ShopProfilePage"));
const AddListingPage = lazyWithReload(() => import("./components/sell/portal/AddListingPage"));
const PrivacyPolicy = lazyWithReload(() => import("./components/privacy/PrivacyPolicy"));
const TermsConditions = lazyWithReload(() => import("./components/terms/TermsConditions"));
const ReturnsPolicy = lazyWithReload(() => import("./components/returns/ReturnsPolicy"));
const ShippingInfo = lazyWithReload(() => import("./components/shipping/ShippingInfo"));
const About = lazyWithReload(() => import("./components/about/About"));
const Verify = lazyWithReload(() => import("./components/auth/Verify"));
const NewsletterUnsubscribe = lazyWithReload(() => import("./components/home/NewsletterUnsubscribe"));
const AllCategories = lazyWithReload(() => import("./components/category/AllCategories"));
const CollectionsPage = lazyWithReload(() => import("./components/collections/CollectionsPage"));
const LivePage = lazyWithReload(() => import("./components/live/LivePage"));
// Soko's chat button is on every page but nobody needs it in the first
// second: its code loads once the page has finished and the browser is idle.
// Plain lazy, NOT lazyWithReload: a side widget must never reload the page,
// and QuietChunkBoundary leaves it out if its file cannot load.
const SokoWidget = lazy(() => import("./components/soko/SokoWidget"));

/* A page whose code failed to load gets a fresh chance on the next page. */
function RouteBoundary({ children }) {
  const { pathname } = useLocation();
  return <RouteChunkBoundary resetKey={pathname}>{children}</RouteChunkBoundary>;
}

/* True once the page has loaded and the browser is idle (or 2 s later). */
export function useAfterLoadIdle() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let idle = 0;
    let timer = 0;
    const start = () => {
      if (typeof window.requestIdleCallback === "function") {
        idle = window.requestIdleCallback(() => setReady(true), { timeout: 2000 });
      } else {
        timer = window.setTimeout(() => setReady(true), 200);
      }
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      if (idle && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idle);
      window.clearTimeout(timer);
    };
  }, []);
  return ready;
}

// How long the hash-target lookup keeps retrying (one attempt per animation
// frame) before giving up — covers a section that's still laying out (image
// loading, lazy content) without hanging indefinitely on a target that will
// never exist.
const HASH_TARGET_MAX_ATTEMPTS = 30; // ~0.5s at 60fps

// Caps how many history entries' scroll positions AnimatedRoutes remembers
// at once. This is pure session-navigation state (never persisted — a
// fresh load always starts with an empty map), but an unbounded Map keyed
// by every location.key visited in a long browsing session would still
// grow without limit; 100 comfortably covers realistic back/forward depth
// with a hard ceiling regardless.
export const MAX_CACHED_POSITIONS = 100;

// Records `key -> y` in `map`, keeping it within MAX_CACHED_POSITIONS via
// least-recently-used eviction. Rejects non-finite/negative values outright
// (a caller bug or a transient bad read should never poison the cache with
// a value that would later be handed straight to scrollTo). Re-setting an
// existing key deletes-then-reinserts it so Map's insertion-order iteration
// doubles as recency order — the oldest (first) key is always the true LRU
// candidate. `protectedKey` (the entry currently needed for the restoration
// in flight) is never evicted even if it happens to be the oldest — this
// can only actually matter in a pathological sequence, but the guard is
// cheap and the requirement is explicit.
export function saveScrollPosition(map, key, y, protectedKey) {
  if (!Number.isFinite(y) || y < 0) return;
  if (map.has(key)) map.delete(key);
  map.set(key, y);
  while (map.size > MAX_CACHED_POSITIONS) {
    const oldestKey = map.keys().next().value;
    if (oldestKey === protectedKey) break;
    map.delete(oldestKey);
  }
}

function scrollToHashTarget(hash, lenis, immediate) {
  const id = decodeURIComponent(hash.slice(1));
  if (!id) return;
  let attempts = 0;
  const tryScroll = () => {
    const el = document.getElementById(id);
    if (el) {
      // lenis.scrollTo reads scroll-margin-top (target) and scroll-padding-top
      // (root) itself — see index.css — so header clearance needs no extra
      // offset here, for either path.
      if (lenis) lenis.scrollTo(el, { immediate });
      else el.scrollIntoView({ behavior: immediate ? "auto" : "smooth", block: "start" });
      return;
    }
    attempts++;
    // Fail safely: never crash, never fall back to a top-reset the user
    // didn't ask for — a hash the app can't resolve just leaves scroll
    // wherever it already was.
    if (attempts < HASH_TARGET_MAX_ATTEMPTS) requestAnimationFrame(tryScroll);
  };
  tryScroll();
}

// Route-change scroll behavior + per-history-entry position memory. The
// entrance-animation machinery that used to live here (two-class
// page-transition/page-entered dance, guarding against a fill-mode:both
// containing-block bug) is gone — page transitions are now native View
// Transitions (src/utils/viewTransition.js), which don't create that
// containing-block problem in the first place. .page-transition is now a
// purely structural class (centered column, ambient glow) — see index.css.
//
// Navigation semantics (PUSH/REPLACE vs POP) come from react-router's own
// useNavigationType — this augments the router's existing behavior rather
// than fighting it, and native browser scroll restoration is turned off
// (history.scrollRestoration = "manual") so it can't race with this.
export function AnimatedRoutes({ children }) {
  const location = useLocation();
  useLayoutEffect(() => {
    document.head.querySelectorAll('script[data-seo="product"], script[data-seo="breadcrumb"], script[data-seo="route"], #ld-json').forEach((el) => el.remove());
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) { robots = document.createElement("meta"); robots.name = "robots"; document.head.appendChild(robots); }
    robots.content = privateRoute(location.pathname) ? "noindex, follow" : "index, follow";
  }, [location.pathname]);
  const navigationType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const lenis = useSmoothScrollLenis();

  // location.key -> scrollY. Keyed per history ENTRY (not pathname) so two
  // different visits to the same route each get their own remembered
  // position — e.g. paging through a listing, opening a product from page 2,
  // then Back needs page 2's scroll, not page 1's.
  const positionsRef = useRef(new Map());
  const currentKeyRef = useRef(location.key);
  const prevPathnameRef = useRef(location.pathname);
  const isInitialMountRef = useRef(true);

  // Take over scroll restoration explicitly rather than leaving the browser
  // to guess — the POP handling below IS the restoration strategy, so the
  // native one must not also fire and race it.
  useEffect(() => {
    if (typeof window === "undefined" || !("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => { window.history.scrollRestoration = previous; };
  }, []);

  // Continuously remember where the CURRENT entry is scrolled to, via a
  // plain native listener rather than React state (no re-renders on scroll).
  // This sees Lenis-driven movement too: Lenis's root mode animates the
  // real window scroll position every frame, it doesn't fake one, so a
  // native `scroll` listener observes it exactly like any other scrolling.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onScroll = () => saveScrollPosition(positionsRef.current, currentKeyRef.current, window.scrollY, currentKeyRef.current);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("is-admin", location.pathname.startsWith("/admin") || location.pathname.startsWith("/sell/portal"));

    const instantScrollTo = (y) => {
      if (lenis) lenis.scrollTo(y, { immediate: true });
      else window.scrollTo({ top: y, behavior: "instant" });
    };

    // Deferred one frame: run after the destination's layout has actually
    // committed and painted, not mid-transition — avoids racing the route's
    // own render/lazy content and gives an accurate scrollHeight to clamp
    // a restored position against.
    //
    // Every ref mutation below happens INSIDE this callback, not
    // synchronously above — React Strict Mode runs this whole effect twice
    // (mount, cleanup, mount again) on the very first render. The first
    // pass's cleanup cancels its rAF before it ever fires, but refs survive
    // that cleanup (same component instance) — so if pathnameChanged/
    // isInitialMount were computed and written to refs synchronously in the
    // effect body, the first (cancelled) pass would still "consume" the
    // initial-mount flag, and the second (real) pass would see it already
    // false and silently skip its hash-scroll/top-reset. Committing state
    // only from the callback that actually runs avoids that entirely: a
    // cancelled pass mutates nothing.
    const raf = requestAnimationFrame(() => {
      const pathnameChanged = location.pathname !== prevPathnameRef.current;
      const isInitialMount = isInitialMountRef.current;

      if (location.hash && (pathnameChanged || isInitialMount)) {
        // Only handle the hash here on an actual page ARRIVAL (a fresh
        // load with a hash in the URL, or a cross-page Link to "/x#y") —
        // Lenis's own anchors:true click handler (see SmoothScroll.jsx)
        // already handles a same-page anchor click directly, synchronously,
        // at click time. Also running our own (necessarily rAF-deferred,
        // so a frame later) scrollTo at the same target used to race that
        // first, already-in-flight animation — normally a harmless no-op
        // via Lenis's own target === targetScroll short-circuit, but
        // empirically NOT reliably so under all layouts/viewport widths
        // (confirmed: a same-page anchor click landed at inconsistent
        // clearance depending on viewport width, going through both paths;
        // going through this one alone is exact at every width tested).
        // Takes priority over both the top-reset and POP-restore below —
        // an explicit anchor always wins. Immediate (no animation) under
        // reduced motion, exactly like every other scroll action here.
        scrollToHashTarget(location.hash, lenis, !lenis);
      } else if (location.hash) {
        // Same-page hash change (Lenis's click handler already owns this).
      } else if (navigationType === "POP") {
        const saved = positionsRef.current.get(location.key);
        if (saved != null) {
          const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          instantScrollTo(Math.min(saved, maxScroll));
        } else if (pathnameChanged) {
          // No remembered position for this entry (e.g. it's the very first
          // one) — land at the top rather than wherever the previous page
          // happened to be scrolled to.
          instantScrollTo(0);
        }
        // POP back to the same pathname with nothing recorded (e.g. a
        // query-only history entry) — leave scroll untouched rather than
        // forcing an unrequested reset.
      } else if (pathnameChanged) {
        // PUSH (a normal click-through) or REPLACE (treated the same —
        // nothing in this app distinguishes REPLACE today, e.g. a login
        // redirect landing on a fresh page should still start at the top).
        instantScrollTo(0);
      }
      // PUSH/REPLACE with the same pathname (e.g. a filter/query change on
      // a listing page) intentionally does nothing — preserves this app's
      // existing behavior of never yanking the scroll position out from
      // under an in-place filter interaction.

      prevPathnameRef.current = location.pathname;
      isInitialMountRef.current = false;
      currentKeyRef.current = location.key;
    });

    return () => cancelAnimationFrame(raf);
    // Intentionally keyed on location.key alone, NOT lenis or navigationType.
    // Those are still read fresh from the closure above whenever this DOES
    // run (they're current as of the same render that produced this
    // location.key), but must not themselves be able to re-trigger this
    // effect: reduced motion toggling on/off changes `lenis` with no
    // navigation involved, and if that were a dependency here it would
    // replay whatever the last navigation's scroll action was (re-jumping
    // to a POP's saved position, re-resetting to top, ...) with no user
    // action behind it — the exact "lose scroll position on a preference
    // change" bug this pass exists to close, just relocated to this effect
    // instead of SmoothScroll's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return <div key={location.key} className="page-transition">{children}</div>;
}

function SiteShell({ cartCount, cartAnimKey, children, sokoUrl }) {
  const location = useLocation();
  const isAdminPanel = location.pathname.startsWith("/admin");
  const isSellerPortal = location.pathname.startsWith("/sell/portal");
  const isAdmin = isAdminPanel || isSellerPortal; // Nav/Footer stay hidden on both — each has its own chrome
  const sokoReady = useAfterLoadIdle();
  return (
    <>
      {/* The site-wide dot-wave background, set per page in
          src/config/siteBackground.js. Decorative only. */}
      <SiteBackground />
      {!isAdmin && <Nav cartCount={cartCount} cartAnimKey={cartAnimKey} />}
      {/* "We'll be back shortly" while the API is down (a release holding
          it). Every page, admin and seller portal included. */}
      <BackShortlyBanner />
      {children}
      {/* One quiet line when a wish could not be saved, or the guest
          sign-in nudge. Lives at the shell so it survives navigation. */}
      <WishNotice />
      {/* Scheduled-maintenance notice; shows and hides itself by date
          (src/config/maintenance.js). Not on admin or the seller portal. */}
      {!isAdmin && <MaintenanceNotice />}
      {!isAdmin && <SiteBackgroundFade />}
      {!isAdmin && <Footer />}
      {sokoReady && (
        <QuietChunkBoundary>
          <Suspense fallback={null}>
            {!isAdminPanel && !isSellerPortal && <SokoWidget apiBase={sokoUrl} mode="buyer" />}
            {isSellerPortal && <SokoWidget apiBase={sokoUrl} mode="seller" />}
          </Suspense>
        </QuietChunkBoundary>
      )}
    </>
  );
}

// The cart-consuming routing tree — a separate component (not part of App
// itself) because useCart() needs useAuth(), which is only available to
// descendants of AuthProvider; App is the component that renders
// AuthProvider, so it can never be inside it.
/* /inbox/:threadId -> /account/inbox?thread=:threadId.
   A component rather than a plain <Navigate>, because the destination
   depends on the path parameter. `replace` so the redirect does not sit in
   the history and trap the back button. */
export function InboxThreadRedirect() {
  const { threadId } = useParams();
  return <Navigate to={inboxPath(threadId)} replace />;
}

/* /account/orders[/:orderId] -> the inbox's Orders folder. My Account never
   had these paths as routes, but its Orders tab did exist, and this is the
   shape a hand-typed or guessed link takes. */
export function AccountOrdersRedirect() {
  const { orderId } = useParams();
  return <Navigate to={ordersPath(orderId)} replace />;
}

function AppRoutes() {
  const { cart, handleAddToCart: addToCart, handleRemove, handleClear, cartError } = useCart();
  const [cartAnimKey, setCartAnimKey] = useState(0);

  useEffect(() => { trackSessionStart(); }, []);

  // Stable across cart changes, so memoised product cards do not all redraw
  // when one item is added (speed brief item 26).
  const handleAddToCart = useCallback((product, qty = 1) => {
    fireATC(product.id, product.category, product.price);
    setCartAnimKey((k) => k + 1);
    addToCart(product, qty);
  }, [addToCart]);

  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);

  return (
    <SmoothScroll>
      <Router>
        <CookieBanner />
        {cartError && (
          <div
            role="alert"
            style={{
              position: "fixed", bottom: "1.5rem", left: "50%", transform: "translateX(-50%)",
              zIndex: 2000, background: "var(--bg-3)", border: "1px solid var(--error)",
              color: "var(--white)", padding: "0.75rem 1.25rem", borderRadius: "10px",
              fontSize: "0.85rem", boxShadow: "0 12px 32px rgba(0,0,0,0.4)", maxWidth: "90vw",
            }}
          >
            {cartError}
          </div>
        )}
        <SiteShell cartCount={cartCount} cartAnimKey={cartAnimKey} sokoUrl={process.env.REACT_APP_SOKO_URL || "http://localhost:8787"}>
          <AnimatedRoutes>
            <RouteBoundary>
            <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
            <Routes>
              <Route path="/"            element={<Home onAddToCart={handleAddToCart} cart={cart} />} />
              <Route path="/categories"  element={<AllCategories />} />
              <Route path="/about"       element={<About />} />
              <Route path="/category/:id" element={<CategoryPage onAddToCart={handleAddToCart} />} />
              <Route path="/search"       element={<SearchPage onAddToCart={handleAddToCart} />} />
              <Route path="/shop/:handle" element={<ShopPage onAddToCart={handleAddToCart} />} />
              <Route path="/collections"  element={<CollectionsPage />} />
              <Route path="/rails/:slug"  element={<RailPage onAddToCart={handleAddToCart} />} />
              <Route path="/suits"       element={<Suits onAddToCart={handleAddToCart} />} />
              <Route path="/shirts"      element={<Shirts onAddToCart={handleAddToCart} />} />
              <Route path="/shoes"       element={<Shoes onAddToCart={handleAddToCart} />} />
              <Route path="/new-arrivals" element={<Newarrivals onAddToCart={handleAddToCart} />} />
              <Route path="/live"        element={<LivePage />} />
              <Route path="/product/:slug" element={<ProductDetailPage onAddToCart={handleAddToCart} />} />
              <Route path="/cart"        element={<Cart cart={cart} onRemove={handleRemove} onClear={handleClear} onAddToCart={handleAddToCart} />} />
              <Route path="/checkout"             element={<Checkout cart={cart} onClear={handleClear} />} />
              <Route path="/checkout/card-return" element={<CardReturn />} />
              <Route path="/login"       element={<Login />} />
              <Route path="/register"    element={<Register />} />
              <Route path="/verify"      element={<Verify />} />
              <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />
              <Route path="/account"     element={<Account />} />
              {/* The inbox — conversations AND order history — is its own
                  full-width page. Every older address for it still lands
                  here: /inbox, /inbox/:threadId, and My Account's former
                  ?tab=inbox / ?tab=orders (handled in Account.jsx). */}
              <Route path="/account/inbox"  element={<InboxPage />} />
              <Route path="/account/orders" element={<AccountOrdersRedirect />} />
              <Route path="/account/orders/:orderId" element={<AccountOrdersRedirect />} />
              <Route path="/deliveries"  element={<Deliveries />} />
              <Route path="/wishes"      element={<WishesPage onAddToCart={handleAddToCart} />} />
              <Route path="/inbox"       element={<Navigate to={inboxPath()} replace />} />
              <Route path="/inbox/:threadId" element={<InboxThreadRedirect />} />
              <Route path="/orders/:id"  element={<OrderTracking />} />
              <Route path="/admin"               element={<Admin />} />
              <Route path="/admin/add-product"  element={<AddProductPage />} />
              <Route path="/admin/edit-product/:id" element={<AddProductPage />} />
              <Route path="/sell"                 element={<SellLanding />} />
              <Route path="/sell/apply"           element={<SellerApplicationWizard />} />
              <Route path="/sell/apply/status"    element={<ApplicationStatusPage />} />
              <Route path="/sell/portal/login"          element={<SellerLoginPage />} />
              <Route path="/sell/portal"                element={<MyListings />} />
              <Route path="/sell/portal/insights"       element={<SellerInsights />} />
              <Route path="/sell/portal/shop"           element={<ShopProfilePage />} />
              <Route path="/sell/portal/add-listing"    element={<AddListingPage />} />
              <Route path="/sell/portal/edit-listing/:id" element={<AddListingPage />} />
              <Route path="/privacy"     element={<PrivacyPolicy />} />
              <Route path="/terms"       element={<TermsConditions />} />
              <Route path="/returns"     element={<ReturnsPolicy />} />
              <Route path="/shipping"    element={<ShippingInfo />} />

              {/* Must stay LAST: React Router v6 ranks by specificity rather
                  than order, so position is not what makes this work — but
                  keeping it last is what makes it obvious to the next reader
                  that everything above is more specific.

                  Before this route existed, an unmatched path rendered the nav
                  and footer around nothing, and the SPA catch-all in
                  public/_redirects serves index.html with a 200 — so every
                  typo and crawler guess was an indexable empty page. This
                  cannot set a 404 status (the app runs after the response has
                  begun); the noindex in NotFound is what keeps it out of the
                  index. /category/{unknown} is different and keeps its real
                  404 — the edge function has the category list, so the SERVER
                  can decide that one. */}
              <Route path="*"            element={<NotFound />} />
            </Routes>
            </Suspense>
            </RouteBoundary>
          </AnimatedRoutes>
        </SiteShell>
      </Router>
    </SmoothScroll>
  );
}

function App() {
  return (
    <ConsentProvider>
    <AuthProvider>
    <SellerAuthProvider>
    <WishlistProvider>
      <AppRoutes />
    </WishlistProvider>
    </SellerAuthProvider>
    </AuthProvider>
    </ConsentProvider>
  );
}

export default App;
